import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  slug: "",
  title: "",
  category: "",
  excerpt: "",
  content: "",
  image_url: "",
  author: "",
  read_time: "5 Min. Lesezeit",
  featured: false,
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[äöü]/g, (c) => ({ ä: "ae", ö: "oe", ü: "ue" }[c]))
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminBlogEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const all = await axios.get(`${API}/admin/blog`, { withCredentials: true });
        const found = all.data.find((p) => p.id === id);
        if (found) setForm(found);
        else toast.error("Artikel nicht gefunden.");
      } catch {
        toast.error("Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (k === "title" && !slugTouched && isNew) next.slug = slugify(v);
      return next;
    });
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.title || !form.slug || !form.category || !form.excerpt || !form.content || !form.image_url || !form.author) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/blog`, form, { withCredentials: true });
        toast.success("Artikel erstellt.");
      } else {
        await axios.patch(`${API}/admin/blog/${id}`, form, { withCredentials: true });
        toast.success("Artikel aktualisiert.");
      }
      navigate("/admin/blog");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Speichern fehlgeschlagen.";
      toast.error(typeof msg === "string" ? msg : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (isNew) return;
    if (!window.confirm(`Artikel „${form.title}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { withCredentials: true });
      toast.success("Artikel gelöscht.");
      navigate("/admin/blog");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-500">Lade…</div>;

  return (
    <div data-testid="admin-blog-edit-page">
      <Link to="/admin/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] hover:text-[#0A192F]" data-testid="back-to-list">
        <ArrowLeft size={16} /> Zurück zur Liste
      </Link>

      <div className="mt-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1E3A8A]">{isNew ? "Neuer Artikel" : "Artikel bearbeiten"}</div>
          <h1 className="mt-3 font-heading font-bold text-[#0A192F] text-3xl lg:text-4xl tracking-tight">
            {form.title || (isNew ? "Neuer Beitrag" : "—")}
          </h1>
        </div>
        {!isNew && (
          <button
            onClick={remove}
            data-testid="delete-post"
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-full text-sm font-semibold"
          >
            <Trash2 size={14} /> Löschen
          </button>
        )}
      </div>

      <form onSubmit={save} className="mt-10 bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 space-y-6">
        <Row>
          <Field label="Titel *">
            <input type="text" value={form.title} onChange={update("title")} data-testid="field-title" className={input} required />
          </Field>
          <Field label="Slug *">
            <input
              type="text"
              value={form.slug}
              onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }}
              data-testid="field-slug"
              className={input}
              required
            />
          </Field>
        </Row>

        <Row>
          <Field label="Kategorie *">
            <input type="text" value={form.category} onChange={update("category")} data-testid="field-category" className={input} required />
          </Field>
          <Field label="Autor:in *">
            <input type="text" value={form.author} onChange={update("author")} data-testid="field-author" className={input} required />
          </Field>
        </Row>

        <Row>
          <Field label="Lesezeit *">
            <input type="text" value={form.read_time} onChange={update("read_time")} data-testid="field-read-time" className={input} required />
          </Field>
          <Field label="Bild-URL *">
            <input type="url" value={form.image_url} onChange={update("image_url")} data-testid="field-image" className={input} required />
          </Field>
        </Row>

        <Field label="Kurzbeschreibung *">
          <textarea rows={3} value={form.excerpt} onChange={update("excerpt")} data-testid="field-excerpt" className={input} required />
        </Field>

        <Field label="Inhalt *">
          <textarea rows={14} value={form.content} onChange={update("content")} data-testid="field-content" className={`${input} font-mono text-sm leading-relaxed`} required />
        </Field>

        <label className="inline-flex items-center gap-3 text-sm" data-testid="field-featured-label">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={update("featured")}
            data-testid="field-featured"
            className="w-4 h-4 accent-[#0A192F]"
          />
          <span className="text-slate-700 font-medium">Als „Featured" Artikel markieren</span>
        </label>

        {form.image_url && (
          <div className="rounded-xl overflow-hidden border border-slate-200 max-w-md">
            <img src={form.image_url} alt="Vorschau" className="w-full h-48 object-cover" />
          </div>
        )}

        <div className="pt-4 flex flex-wrap gap-3 border-t border-slate-200">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-post"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A192F] text-white rounded-full font-semibold hover:bg-[#1E3A8A] disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? "Wird gespeichert…" : isNew ? "Artikel erstellen" : "Änderungen speichern"}
          </button>
          <Link to="/admin/blog" className="inline-flex items-center px-6 py-3 border border-slate-300 text-slate-700 rounded-full font-semibold">
            Abbrechen
          </Link>
        </div>
      </form>
    </div>
  );
}

const input =
  "w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:border-[#0A192F] focus:outline-none focus:ring-1 focus:ring-[#0A192F]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#1E3A8A] mb-2">{label}</span>
      {children}
    </label>
  );
}

function Row({ children }) {
  return <div className="grid sm:grid-cols-2 gap-5">{children}</div>;
}
